import { fetchDependencies } from '../modal.forms';
import { ADMIN_MODAL_FORMS, ADMIN_FLASH } from '../../../constants/actions';
import { apiActionCreator } from '../../helpers';
import { partners } from '../../../services/api.admin';
import { selectedSelector } from '../../../selectors/admin/tables';
import { refresh } from '../tables';

export function fetchCopyClearanceDependencies(_c, eventId) {
    const { events } = require('../../../services/api.admin');
    return fetchDependencies([() => partners.getAllForEvent(eventId), () => events.getCategoryConfig(eventId)]);
}

export function fetchVisibleCategoriesDependencies(context, eventId) {
    const { events } = require('../../../services/api.admin');
    return fetchDependencies([() => events.getCategoryConfig(eventId)]);
}

export function copyClearance(data, eventId, categoryConfig) {
    return async (dispatch, getState) => {
        dispatch({ type: ADMIN_MODAL_FORMS.SUBMIT });
        const selection = selectedSelector(getState());
        const { events } = require('../../../services/api.admin');

        return apiActionCreator(
            dispatch,
            async () => {
                if (data.copyVisibleCategories && categoryConfig?.categories) {
                    const sourcePartnerId = data.partnerId;
                    const visibleCategoryIds = categoryConfig.categories
                        .filter((category) => {
                            if (category.visibleByDefault) return false;
                            const visibleByPartners = category.visibleByPartners || [];
                            return visibleByPartners.includes(sourcePartnerId);
                        })
                        .map((cat) => cat.id);

                    const updatedCategories = categoryConfig.categories.map((category) => {
                        if (category.visibleByDefault) {
                            return category;
                        }

                        const visibleByPartners = category.visibleByPartners || [];
                        const shouldBeVisible = visibleCategoryIds.includes(category.id);

                        if (shouldBeVisible) {
                            // Add destination partners to this category
                            const newPartners = selection.filter((id) => !visibleByPartners.includes(id));
                            return {
                                ...category,
                                visibleByPartners: [...visibleByPartners, ...newPartners]
                            };
                        } else {
                            // Remove destination partners from this category
                            const filteredPartners = visibleByPartners.filter((id) => !selection.includes(id));
                            return {
                                ...category,
                                visibleByPartners: filteredPartners
                            };
                        }
                    });

                    const updatedConfig = {
                        ...categoryConfig,
                        eventId,
                        categories: updatedCategories
                    };

                    await events.updateCategoryConfig(eventId, updatedConfig);
                }

                if (data.copyClearances) {
                    await partners.copyClearance(data.partnerId, selection, eventId, data.copyFields || {});
                }
            },
            () => {
                dispatch({ type: ADMIN_MODAL_FORMS.SUCCESS });

                let message = '';
                if (data.copyClearances && data.copyVisibleCategories) {
                    message = 'Clearances and visible categories successfully copied!';
                } else if (data.copyClearances) {
                    message = 'Clearances successfully copied!';
                } else if (data.copyVisibleCategories) {
                    message = 'Visible categories successfully copied!';
                }

                dispatch({
                    type: ADMIN_FLASH.OPEN,
                    message
                });
                dispatch(refresh());

                const { fetchLineup } = require('./lineup');
                const { fetchCategoryConfig } = require('./event.clearance');
                dispatch(fetchLineup(eventId));
                dispatch(fetchCategoryConfig(eventId));
            }
        );
    };
}

export function updateVisibleCategories(data, eventId, partnerId, categoryConfig) {
    return async (dispatch) => {
        dispatch({ type: ADMIN_MODAL_FORMS.SUBMIT });
        const { events } = require('../../../services/api.admin');

        return apiActionCreator(
            dispatch,
            async () => {
                if (!categoryConfig?.categories) {
                    throw new Error('No clearance configuration found for this event');
                }

                const updatedCategories = categoryConfig.categories.map((category) => {
                    if (category.visibleByDefault) {
                        return category;
                    }

                    const visibleByPartners = category.visibleByPartners || [];
                    const isSelected = data.categoryIds.includes(category.id);
                    const isCurrentlyVisible = visibleByPartners.includes(partnerId);

                    if (isSelected && !isCurrentlyVisible) {
                        return {
                            ...category,
                            visibleByPartners: [...visibleByPartners, partnerId]
                        };
                    }

                    if (!isSelected && isCurrentlyVisible) {
                        return {
                            ...category,
                            visibleByPartners: visibleByPartners.filter((id) => id !== partnerId)
                        };
                    }

                    return category;
                });

                const updatedConfig = {
                    ...categoryConfig,
                    eventId,
                    categories: updatedCategories
                };

                return await events.updateCategoryConfig(eventId, updatedConfig);
            },
            () => {
                dispatch({ type: ADMIN_MODAL_FORMS.SUCCESS });
                dispatch({
                    type: ADMIN_FLASH.OPEN,
                    message: 'Visible categories updated successfully!'
                });
                dispatch(refresh());

                const { fetchCategoryConfig } = require('./event.clearance');
                dispatch(fetchCategoryConfig(eventId));
            }
        );
    };
}

export function bulkUpdateVisibleCategories(data, eventId, partnerIds, categoryConfig) {
    return async (dispatch) => {
        dispatch({ type: ADMIN_MODAL_FORMS.SUBMIT });
        const { events } = require('../../../services/api.admin');

        return apiActionCreator(
            dispatch,
            async () => {
                if (!categoryConfig?.categories) {
                    throw new Error('No clearance configuration found for this event');
                }

                const updatedCategories = categoryConfig.categories.map((category) => {
                    if (category.visibleByDefault) {
                        return category;
                    }

                    const visibleByPartners = category.visibleByPartners || [];
                    const isSelected = data.categoryIds.includes(category.id);

                    let updatedVisibleByPartners = visibleByPartners.filter((id) => !partnerIds.includes(id));

                    if (isSelected) {
                        updatedVisibleByPartners = [...updatedVisibleByPartners, ...partnerIds];
                    }

                    return {
                        ...category,
                        visibleByPartners: updatedVisibleByPartners
                    };
                });

                const updatedConfig = {
                    ...categoryConfig,
                    eventId,
                    categories: updatedCategories
                };

                return await events.updateCategoryConfig(eventId, updatedConfig);
            },
            () => {
                dispatch({ type: ADMIN_MODAL_FORMS.SUCCESS });
                dispatch({
                    type: ADMIN_FLASH.OPEN,
                    message: `Visible categories updated for ${partnerIds.length} media partner${
                        partnerIds.length === 1 ? '' : 's'
                    }!`
                });
                dispatch(refresh());

                const { fetchCategoryConfig } = require('./event.clearance');
                dispatch(fetchCategoryConfig(eventId));
            }
        );
    };
}

export async function addPartnersToClearanceCategories(eventId, partnerIds, categoryIds) {
    if (!categoryIds?.length) {
        return;
    }

    const { events } = require('../../../services/api.admin');
    const response = await events.getCategoryConfig(eventId);
    const categoryConfig = response.json;

    if (!categoryConfig?.categories) {
        return;
    }

    const updatedCategories = categoryConfig.categories.map((category) => {
        if (category.visibleByDefault || !categoryIds.includes(category.id)) {
            return category;
        }

        const visibleByPartners = category.visibleByPartners || [];
        const newPartnerIds = partnerIds.filter((id) => !visibleByPartners.includes(id));

        return {
            ...category,
            visibleByPartners: [...visibleByPartners, ...newPartnerIds]
        };
    });

    const updatedConfig = {
        ...categoryConfig,
        eventId,
        categories: updatedCategories
    };

    await events.updateCategoryConfig(eventId, updatedConfig);
}

export async function removePartnersFromClearanceCategories(eventId, partnerIds) {
    try {
        const { events } = require('../../../services/api.admin');
        const response = await events.getCategoryConfig(eventId);
        const categoryConfig = response.json;

        if (!categoryConfig?.categories) {
            return;
        }

        const updatedCategories = categoryConfig.categories.map((category) => {
            if (!category.visibleByPartners?.length) {
                return category;
            }

            return {
                ...category,
                visibleByPartners: category.visibleByPartners.filter((id) => !partnerIds.includes(id))
            };
        });

        const updatedConfig = {
            ...categoryConfig,
            eventId,
            categories: updatedCategories
        };

        await events.updateCategoryConfig(eventId, updatedConfig);
    } catch (error) {
        console.error('Error updating clearance config:', error);
    }
}
